import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import { dissolveReveal } from "../dissolve"

export default class extends Controller {
  static targets = ["input", "previews", "dropzone", "cameraIcon", "noJsList"]
  static values = { url: String, fieldName: String }

  connect() {
    this.pendingUploads = 0
    // Progressive enhancement: hide the native input + no-JS list, reveal the JS dropzone
    this.inputTarget.classList.add("hidden")
    this.dropzoneTarget.classList.remove("hidden")
    if (this.hasNoJsListTarget) this.noJsListTarget.classList.add("hidden")

    const form = this.element.closest("form")
    if (form) {
      this._submitHandler = (e) => this.beforeSubmit(e)
      form.addEventListener("submit", this._submitHandler)
    }
  }

  disconnect() {
    // Restore native input in case JS is removed at runtime
    this.inputTarget.classList.remove("hidden")
    this.dropzoneTarget.classList.add("hidden")
    if (this.hasNoJsListTarget) this.noJsListTarget.classList.remove("hidden")

    const form = this.element.closest("form")
    if (form && this._submitHandler) {
      form.removeEventListener("submit", this._submitHandler)
    }
  }

  beforeSubmit(event) {
    if (this.pendingUploads > 0) {
      event.preventDefault()
      event.stopPropagation()
      this.showDropzoneHint("Please wait for uploads to finish…")
    }
  }

  openFile(event) {
    const url = event.currentTarget.dataset.url
    if (url) window.open(url, "_blank", "noopener,noreferrer")
  }

  browse(event) {
    if (event.target.closest("[data-state]")) return
    this.inputTarget.click()
  }

  inputChanged(event) {
    this.handleFiles(event.target.files)
    event.target.value = ""
  }

  dragover(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("border-warm-amber-500", "bg-hearth-600")
    this.dropzoneTarget.classList.remove("border-hearth-500")
  }

  dragleave(event) {
    if (!this.dropzoneTarget.contains(event.relatedTarget)) {
      this.dropzoneTarget.classList.remove("border-warm-amber-500", "bg-hearth-600")
      this.dropzoneTarget.classList.add("border-hearth-500")
    }
  }

  drop(event) {
    if (event.dataTransfer.files.length === 0) return
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-warm-amber-500", "bg-hearth-600")
    this.dropzoneTarget.classList.add("border-hearth-500")
    this.handleFiles(event.dataTransfer.files)
  }

  handleFiles(files) {
    Array.from(files).forEach(file => this.uploadFile(file))
  }

  uploadFile(file) {
    this.pendingUploads++
    const tile = this.buildTile(file)
    this.previewsTarget.appendChild(tile)
    this.updateDropzoneState()

    const upload = new DirectUpload(file, this.urlValue, {
      directUploadWillStoreFileWithXHR: (xhr) => {
        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            const percent = Math.round((event.loaded / event.total) * 100)
            this.updateProgress(tile, percent)
          }
        })
      }
    })

    upload.create((error, blob) => {
      this.pendingUploads--
      if (error) {
        this.markError(tile)
      } else {
        this.markDone(tile, blob)
      }
    })
  }

  buildTile(file) {
    const tile = this.createTileShell("uploading")

    const preview = this.createPreviewShell()
    if (file.type.startsWith("image/")) {
      const objectUrl = URL.createObjectURL(file)
      const img = document.createElement("img")
      img.src = objectUrl
      img.className = "w-full h-full object-cover"
      img.draggable = false
      img.dataset.objectUrl = objectUrl
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl)
        delete img.dataset.objectUrl
        img.replaceWith(this.createExtBadge(file.name))
      }
      preview.appendChild(img)
    } else {
      preview.appendChild(this.createExtBadge(file.name))
    }
    tile.appendChild(preview)

    const overlay = document.createElement("div")
    overlay.className = "absolute inset-0 bg-black/25 flex items-center justify-center"
    overlay.dataset.progressOverlay = true
    const badge = document.createElement("div")
    badge.className = "bg-hearth-900/90 rounded-lg px-2.5 py-1 text-xs font-semibold text-hearth-50 tabular-nums"
    badge.dataset.progressText = true
    badge.textContent = "0%"
    overlay.appendChild(badge)
    tile.appendChild(overlay)

    tile.appendChild(this.createInfo(file.name, file.size))
    tile.appendChild(this.createRemoveButton())

    return tile
  }

  createTileShell(state) {
    const el = document.createElement("div")
    el.className = "relative flex flex-col rounded-xl border border-hearth-500 bg-hearth-700 overflow-hidden cursor-pointer"
    el.dataset.state = state
    el.draggable = true
    el.setAttribute("data-action", "click->file-upload#openFile dragstart->document-buckets#tileDragStart")
    return el
  }

  createPreviewShell() {
    const el = document.createElement("div")
    el.className = "aspect-square w-full bg-hearth-600 flex items-center justify-center overflow-hidden"
    el.dataset.previewArea = true
    return el
  }

  createExtBadge(filename) {
    const el = document.createElement("span")
    el.className = "text-xs font-bold text-hearth-300 uppercase tracking-widest"
    el.textContent = filename.split(".").pop() || "file"
    return el
  }

  createInfo(filename, byteSize) {
    const el = document.createElement("div")
    el.className = "px-2.5 py-2"
    const name = document.createElement("p")
    name.className = "text-xs font-medium text-hearth-100 truncate"
    name.textContent = filename
    const size = document.createElement("p")
    size.className = "text-xs text-hearth-300"
    size.textContent = this.formatSize(byteSize)
    el.appendChild(name)
    el.appendChild(size)
    return el
  }

  createRemoveButton() {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = "absolute top-1.5 right-1.5 rounded-full bg-hearth-900/80 p-0.5 text-hearth-300 hover:text-red-400 transition-colors"
    btn.setAttribute("data-action", "file-upload#remove")
    btn.setAttribute("aria-label", "Remove file")
    btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
      <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
    </svg>`
    return btn
  }

  updateProgress(tile, percent) {
    const text = tile.querySelector("[data-progress-text]")
    if (text) text.textContent = `${percent}%`
  }

  markDone(tile, blob) {
    tile.dataset.state = "done"
    tile.dataset.url = `/rails/active_storage/blobs/redirect/${blob.signed_id}/${encodeURIComponent(blob.filename)}`
    tile.querySelector("[data-progress-overlay]")?.remove()

    const img = tile.querySelector("img[data-object-url]")
    if (img) URL.revokeObjectURL(img.dataset.objectUrl)

    const input = document.createElement("input")
    input.type = "hidden"
    input.name = this.fieldNameValue
    input.value = blob.signed_id
    tile.appendChild(input)

    this.swapThumbnail(tile, blob)
  }

  async swapThumbnail(tile, blob) {
    // Non-image types (DOCX, CSV, etc.) have no renderable thumbnail — the
    // server would redirect to the raw blob, which breaks as an <img> src.
    // Keep the ext-badge that buildTile already placed and skip the dissolve.
    if (!blob.content_type.startsWith("image/")) return

    const preview = tile.querySelector("[data-preview-area]")
    if (!preview) return

    // Absolutely positioned so it floats above the dissolve layers and remains
    // visible throughout the animation, disappearing only when the final image
    // replaces everything at the end of the reveal.
    const spinnerWrap = document.createElement("div")
    spinnerWrap.dataset.thumbnailSpinner = true
    spinnerWrap.style.cssText = "position:absolute;inset:0;z-index:10;display:flex;align-items:center;justify-content:center;pointer-events:none;"
    const spinner = document.createElement("div")
    spinner.className = "animate-spin h-5 w-5 rounded-full border-2 border-hearth-500 border-t-warm-amber-400"
    spinnerWrap.appendChild(spinner)
    preview.appendChild(spinnerWrap)

    const url = `/thumbnail?signed_id=${encodeURIComponent(blob.signed_id)}`
    const { cancel, done } = dissolveReveal(url, preview)
    tile._cancelDissolve = cancel
    done.then(() => preview.querySelector("[data-thumbnail-spinner]")?.remove())
  }

  markError(tile) {
    tile.dataset.state = "error"
    tile.classList.add("border-red-300")
    const overlay = tile.querySelector("[data-progress-overlay]")
    if (overlay) {
      overlay.className = "absolute inset-0 bg-red-500/20 flex items-center justify-center"
      const badge = overlay.querySelector("[data-progress-text]")
      if (badge) {
        badge.className = "bg-white/90 rounded-lg px-2.5 py-1 text-xs font-semibold text-red-600"
        badge.textContent = "Failed"
      }
    }
  }

  remove(event) {
    event.stopPropagation()
    const tile = event.currentTarget.closest("[data-state]")
    if (!tile) return
    if (tile.dataset.state === "uploading") {
      this.pendingUploads = Math.max(0, this.pendingUploads - 1)
    }
    const img = tile.querySelector("img[data-object-url]")
    if (img) URL.revokeObjectURL(img.dataset.objectUrl)
    tile._cancelDissolve?.()
    tile.remove()
    this.updateDropzoneState()
  }

  updateDropzoneState() {
    const hasTiles = this.previewsTarget.children.length > 0
    this.previewsTarget.classList.toggle("hidden", !hasTiles)
    this.cameraIconTarget.classList.toggle("hidden", hasTiles)
  }

  showDropzoneHint(message) {
    const hint = this.dropzoneTarget.querySelector("[data-hint]")
    if (hint) hint.textContent = message
  }

  formatSize(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }
}
